import { api } from '@/lib/apiClient'
import type { Envelope } from '@/lib/apiTypes'
import { extractApiStatus } from '@/lib/apiError'
import type { Subscriber } from '@/features/auth/types'
import { TIERS, type TierCode } from '@alerte-immo/plans'

export type SubscriptionStatus =
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'incomplete'
  /** Stripe a épuisé ses relances sans paiement : servi en Découverte jusqu'au règlement. */
  | 'unpaid'

/** Only the two paid tiers can be checked out / switched to. */
export type PaidTier = Exclude<TierCode, 'decouverte'>

/**
 * Current plan as returned by GET /account/subscription. Mirrors the tier model:
 * one plan per subscriber, with quota + trial + billing state. A 404 (no plan
 * yet) is normalised to `null` by getSubscription → treated as free Découverte.
 */
export interface SubscriptionPlan {
  tier: TierCode
  status: SubscriptionStatus
  currentPeriodEnd: string | null
  canceledAt: string | null
  /**
   * Annulation programmée à échéance : signal fiable (≠ status, qui reste
   * `trialing` même après annulation pendant l'essai). false par défaut.
   */
  cancelAtPeriodEnd: boolean
  /** Fin d'accès (fin d'essai ou fin de période payante), ISO8601 ou null. */
  cancelAt: string | null
  keepWarm: boolean
  emailDeliveryPaused: boolean
  paid: boolean
  priceCents: number
  trial: { active: boolean; endsAt: string | null }
  quota: { searchesUsed: number; searchesMax: number }
  /** Préférence personnelle de volume d'envoi : null ⇒ défaut du tier (= dailyBudgetCap). */
  dailyMax: number | null
  /** Budget réellement appliqué : dailyMax plafonné par le cap, sinon le cap du tier. */
  effectiveDailyBudget: number
  /** Plafond du tier (borne haute du réglage dailyMax), piloté par l'admin. */
  dailyBudgetCap: number
}

/** GET current plan. 404 (no plan yet) → null = free Découverte default. */
export async function getSubscription(): Promise<SubscriptionPlan | null> {
  try {
    const res = await api.get<Envelope<SubscriptionPlan>>('/account/subscription')
    return res.data.data
  } catch (err: unknown) {
    if (extractApiStatus(err) === 404) return null
    throw err
  }
}

/** Statuts d'un palier payant qui ouvrent l'accès aux fonctions premium (essai inclus). */
export const ACTIVE_STATUSES: SubscriptionStatus[] = ['active', 'trialing', 'past_due']

/**
 * Niveau réellement servi par le back. `tier`/`paid` restent ceux du plan facturé
 * quel que soit le statut : seuls l'essai, l'actif et le retard de paiement
 * servent `tier` ; `unpaid`, `canceled` et `incomplete` sont servis en Découverte
 * (fiche 402, export 402, catalogue/quota/rayon Découverte).
 */
export function servedTier(plan: SubscriptionPlan | null | undefined): TierCode {
  return plan != null && ACTIVE_STATUSES.includes(plan.status) ? plan.tier : 'decouverte'
}

/** Vrai si le plan est un palier payant dans un état actif (essai compris). */
export function isSubscriptionPaidActive(plan: SubscriptionPlan | null | undefined): boolean {
  return TIERS[servedTier(plan)].paid
}

/** Vrai si le plan, actif, ouvre l'export CSV (capability réservée aux paliers payants). */
export function canExportListings(plan: SubscriptionPlan | null | undefined): boolean {
  return TIERS[servedTier(plan)].csvExport
}

/**
 * Start a Stripe Checkout for a paid tier. Returns the URL to redirect to.
 * One free trial per subscriber: bills immediately once `trial.endsAt` is set
 * or a Stripe subscription existed. 409 (CONFLICT) while the plan is `unpaid`.
 */
export async function startCheckout(tier: PaidTier): Promise<string> {
  const res = await api.post<Envelope<{ checkoutUrl: string }>>(
    '/account/subscription/checkout',
    { tier },
  )
  return res.data.data.checkoutUrl
}

/**
 * Réconcilie le plan local depuis la session Stripe au retour du paiement, sans
 * attendre le webhook (qui reste la source de vérité). Idempotent. Renvoie le
 * plan à jour, au même format que getSubscription.
 * Erreurs : 409 (session pas encore finalisée côté Stripe, ré-essayable),
 * 403 (session d'un autre abonné, ne pas réessayer), 422 (session_id vide).
 */
export async function syncSubscription(sessionId: string): Promise<SubscriptionPlan> {
  const res = await api.post<Envelope<SubscriptionPlan>>('/account/subscription/sync', {
    session_id: sessionId,
  })
  return res.data.data
}

/**
 * Switch between paid tiers (Veille ↔ Prospection), prorated: an upgrade
 * invoices the difference immediately, a downgrade credits the next invoice.
 * 409 (CONFLICT) while the plan is `unpaid`, as for startCheckout.
 */
export async function changePlan(tier: PaidTier): Promise<SubscriptionPlan> {
  const res = await api.post<Envelope<SubscriptionPlan>>(
    '/account/subscription/change',
    { tier },
  )
  return res.data.data
}

/** Cancel the paid plan at period end (then falls back to free Découverte). */
export async function cancelPlan(): Promise<SubscriptionPlan> {
  const res = await api.post<Envelope<SubscriptionPlan>>('/account/subscription/cancel')
  return res.data.data
}

/** Statut Stripe d'une facture, tel que renvoyé par l'API. */
export type InvoiceStatus = 'paid' | 'open' | 'void' | 'draft' | 'uncollectible'

/**
 * Facture Stripe servie en direct par /account/subscription/invoices (pas de
 * cache local). Montants en centimes, dates ISO8601 UTC. Les URLs sont hébergées
 * par Stripe : `hostedInvoiceUrl` (consultation) et `invoicePdf` (téléchargement).
 */
export interface StripeInvoice {
  id: string
  number: string | null
  status: InvoiceStatus
  paid: boolean
  currency: string
  amountDue: number
  amountPaid: number
  created: string
  periodStart: string
  periodEnd: string
  /** null pour les factures draft/void (non finalisées côté Stripe). */
  hostedInvoiceUrl: string | null
  invoicePdf: string | null
}

/**
 * Liste des factures Stripe de l'abonné (24 plus récentes, de la plus récente à
 * la plus ancienne). Un abonné sans client Stripe (Découverte jamais facturé)
 * reçoit un tableau vide avec un 200.
 */
export async function getInvoices(): Promise<StripeInvoice[]> {
  const res = await api.get<Envelope<StripeInvoice[]>>('/account/subscription/invoices')
  return res.data.data
}

/**
 * Crée une session du portail de facturation Stripe et renvoie son URL (éphémère,
 * à générer juste avant la redirection — ne jamais mettre en cache). 422
 * (VALIDATION_ERROR) si l'abonné n'a pas de client Stripe, 502 (BILLING_ERROR)
 * si Stripe est indisponible.
 */
export async function createBillingPortalSession(): Promise<string> {
  const res = await api.post<Envelope<{ portalUrl: string }>>('/account/subscription/portal')
  return res.data.data.portalUrl
}

/**
 * Préférence de volume d'envoi : plafond personnel de biens par e-mail.
 * `dailyMax` entre 1 et le cap du tier ; `null` réinitialise au défaut du tier.
 * Renvoie une 422 (VALIDATION_ERROR) hors bornes.
 */
export async function updatePreferences(dailyMax: number | null): Promise<SubscriptionPlan> {
  const res = await api.patch<Envelope<SubscriptionPlan>>(
    '/account/subscription/preferences',
    { dailyMax },
  )
  return res.data.data
}

/**
 * Rectification de l'adresse e-mail (RGPD art. 16), en deux temps. Étape 1 :
 * demande l'envoi d'un code à 6 chiffres sur la NOUVELLE adresse (sert aussi de
 * renvoi). Le compte n'est pas modifié à ce stade.
 * Erreurs : 422 (mal formé / identique), 409 (déjà utilisée), 429 (cooldown).
 */
export async function requestEmailChange(
  newEmail: string,
): Promise<{ newEmail: string; message: string }> {
  const res = await api.post<Envelope<{ newEmail: string; message: string }>>(
    '/account/email',
    { newEmail },
  )
  return res.data.data
}

/**
 * Étape 2 : confirme le changement avec le code reçu. Renvoie l'abonné mis à
 * jour (nouvel email, emailVerified=true). Erreurs : 401 (code invalide/expiré),
 * 409 (adresse prise entre-temps), 422 (format), 429 (trop de tentatives).
 */
export async function verifyEmailChange(code: string): Promise<Subscriber> {
  const res = await api.post<Envelope<Subscriber>>('/account/email/verify', { code })
  return res.data.data
}
